<template>
  <div>
    <fieldset v-if="variant === 'checkbox'" v-hubble="'checkbox'">
      <div class="space-y-5">
        <div class="relative flex items-center gap-2">
          <div class="flex h-6 items-center justify-center">
            <Switch
              v-bind="$attrs"
              v-model="enabled"
              :disabled="disableInteraction"
              :style="checkboxStyle"
              :class="checkboxClasses"
              @update:model-value="handleChange"
            >
              <span
                aria-hidden="true"
                :class="enabled ? '' : 'hidden'"
                class="pointer-events-none h-[15px] w-[15px] absolute inset-0 m-auto text-white ring-0"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="h-full w-full">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </span>
            </Switch>
          </div>
          <button
            v-if="label"
            type="button"
            :disabled="disableInteraction"
            class="text-sm text-typography-primary text-left disabled:text-typography-disabled disabled:cursor-not-allowed"
            @click="handleChange(!enabled)"
          >
            {{ label }}
          </button>
        </div>
      </div>
    </fieldset>
    <div v-else class="flex items-center gap-2" v-hubble="'toggle'">
      <Switch
        v-model="enabled"
        :disabled="disableInteraction"
        @update:model-value="handleChange"
        :name="name"
        :style="toggleStyle"
        :class="toggleClasses"
      >
        <span class="sr-only">Use setting</span>
        <span
          :class="[
            enabled ? translateSizeClasses : 'translate-x-0',
            'pointer-events-none relative inline-block h-4 w-4 transform rounded-full bg-neutral-white shadow ring-0 transition duration-200 ease-in-out',
          ]"
        >
          <span
            :class="[
              enabled ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
              'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity text-neutral-300',
            ]"
            class="fill-current"
            aria-hidden="true"
            v-hubble="'toggle-off'"
          >
            <XMark class="!h-[7.2px] !w-[7.2px] text-neutral-300" />
          </span>
          <span
            v-hubble="'toggle-on'"
            :class="[
              enabled ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
              'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
            ]"
            aria-hidden="true"
          >
            <PulseIconCheck class="!h-[6px] !w-[8.4px] text-primary-700" />
          </span>
        </span>
      </Switch>

      <div class="text-[13px] flex-shrink-0">
        {{ label }}
      </div>
      <div v-if="disableInteractionReason" class="text-[11px] text-danger-700">
        {{ disableInteractionReason }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Switch } from '@headlessui/vue';
import { isHexColor, isTailwindClass } from '@pulse/utils/branded-types';

const props = defineProps({
  variant: {
    type: String,
    validator: (variant: string) => ['default', 'icon', 'checkbox'].includes(variant),
    default: 'default',
  },
  name: {
    type: String,
  },
  size: {
    type: String,
    default: 'medium',
    validator: (size: string) => ['small', 'medium', 'large'].includes(size),
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  initialValue: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  enabledBgColor: {
    type: String,
    default: 'bg-primary-700',
  },
  disabledBgColor: {
    type: String,
    default: 'bg-neutral-400',
  },
  disableInteraction: {
    type: Boolean,
    default: false,
  },
  disableInteractionReason: {
    type: String,
    default: undefined,
  },
  borderColor: {
    type: String,
    default: 'border-primary-700',
  },
});

const enabled = ref(false);
const emit = defineEmits(['update:modelValue', 'change']);

const getColorStyle = (color: string, property: string): Record<string, string> => {
  return isHexColor(color) ? { [property]: color } : {};
};

const getColorClass = (color: string): string => {
  return isTailwindClass(color) ? color : '';
};

const checkboxStyle = computed(() => {
  const styles: Record<string, string> = {
    '--checkbox-hover-border-color': isHexColor(props.borderColor) ? props.borderColor : 'var(--primary-700)',
  };

  if (enabled.value) {
    Object.assign(
      styles,
      getColorStyle(props.enabledBgColor, 'backgroundColor'),
      getColorStyle(props.borderColor, 'borderColor'),
    );
  }

  return styles;
});

const checkboxClasses = computed(() => {
  const baseClasses = [
    'relative h-[24px] w-[24px] shrink-0 cursor-pointer rounded border transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 custom-toggle',
    { 'selector-checkbox': enabled.value },
  ];

  // Unchecked checkboxes read as an empty outlined box (white surface + neutral border), never a filled
  // grey square. bg-neutral-white is a token, so it still themes correctly in dark mode.
  const colorClasses = enabled.value
    ? [getColorClass(props.enabledBgColor), getColorClass(props.borderColor)]
    : ['bg-neutral-white'];

  const stateClasses = props.error ? ['border-danger-700'] : !enabled.value ? ['border-neutral-300'] : [];

  return [...baseClasses, ...colorClasses.filter(Boolean), ...stateClasses];
});

const toggleStyle = computed(() => {
  const color = enabled.value ? props.enabledBgColor : props.disabledBgColor;
  return getColorStyle(color, 'backgroundColor');
});

const toggleClasses = computed(() => {
  const baseClasses = [
    'relative inline-flex !h-5 flex-shrink-0 cursor-pointer rounded-full !border-2 !border-transparent transition-colors duration-200 ease-in-out focus:!outline-none',
    'disabled:!cursor-not-allowed',
  ];

  const sizeClasses = Object.entries(sizeClass.value)
    .filter(([, condition]) => condition)
    .map(([className]) => className);

  const color = enabled.value ? props.enabledBgColor : props.disabledBgColor;
  const colorClass = getColorClass(color);

  return [...baseClasses, ...sizeClasses, colorClass].filter(Boolean);
});

watch(
  () => props.initialValue,
  newValue => {
    enabled.value = newValue;
  },
  {
    immediate: true,
  },
);

watch(
  () => props.modelValue,
  newValue => {
    enabled.value = newValue;
  },
  {
    immediate: true,
  },
);

function handleChange(isEnabled: boolean) {
  enabled.value = isEnabled;
  emit('change', isEnabled);
  emit('update:modelValue', isEnabled);
}

const sizeClass = computed(() => {
  return {
    '!w-8': props.size === 'small',
    '!w-9': props.size === 'medium',
    '!w-10': props.size === 'large',
  };
});

const translateSizeClasses = computed(() => {
  return {
    'translate-x-3': props.size === 'small',
    'translate-x-4': props.size === 'medium',
    'translate-x-5': props.size === 'large',
  };
});
</script>

<style>
.custom-toggle:hover {
  border-color: var(--checkbox-hover-border-color);
}
</style>
